import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import API_BASE from "../api";
import { MapLegend, anomalyColor, formatAnomaly } from "./mapScale";

// City page hero: the city's name and headline number beside a map of
// nClimGrid's ~4 km cells inside the city limit, colored by how much warmer or
// cooler than the 1951–80 normal it is (Today = NDFD forecast, Past week =
// observed). Cells are clipped to the city outline. Coastal no-data cells that
// touch the city are filled from their land neighbors (display only); anything
// still without data (mostly water) is hatched. Map data: GET /city-map/:city_id (nClimGrid
// backends only). Without a map, the hero falls back to the centered header.

const MAX_HEIGHT = 400; // keeps the dashboard tabs in view on a laptop

function useWidth(ref) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

// d3 treats a clockwise ring as an exterior and counter-clockwise as a hole,
// the opposite of the GeoJSON spec the Census outlines follow. A ring wound the
// wrong way covers more than half the globe, so reverse any ring that does.
function rewindForD3(geometry) {
  const fixPolygon = (rings) =>
    rings.map((ring, i) => {
      const large = d3.geoArea({ type: "Polygon", coordinates: [ring] }) > 2 * Math.PI;
      return (i === 0 ? large : !large) ? [...ring].reverse() : ring;
    });
  if (geometry.type === "Polygon") return { ...geometry, coordinates: fixPolygon(geometry.coordinates) };
  if (geometry.type === "MultiPolygon") return { ...geometry, coordinates: geometry.coordinates.map(fixPolygon) };
  return geometry;
}

// Keep the city's substantial land areas only. Some city limits include tiny,
// far-off pieces (San Francisco's Farallon Islands, ~40 km offshore) that
// would shrink the city to a corner of the map. Parts under MIN_PART_SHARE of
// the largest part are dropped from the drawing.
const MIN_PART_SHARE = 0.02;
function mainParts(geometry) {
  if (geometry.type !== "MultiPolygon") return geometry;
  const areas = geometry.coordinates.map((polygon) => d3.geoArea({ type: "Polygon", coordinates: polygon }));
  const largest = d3.max(areas);
  const kept = geometry.coordinates.filter((_, i) => areas[i] >= largest * MIN_PART_SHARE);
  return kept.length === 1 ? { type: "Polygon", coordinates: kept[0] } : { type: "MultiPolygon", coordinates: kept };
}

// nClimGrid marks a cell as water when its center is offshore, so a thin coastal
// strip of a city (San Francisco's Ocean Beach, the Presidio) can fall in a
// no-data cell. For display only, such a cell that touches the city takes the
// mean of its land neighbors (8 around it); it is flagged as estimated. City
// statistics keep using the measured cells only.
function outlinePoints(geometry) {
  const polygons = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
  return polygons.flatMap((polygon) => polygon.flat());
}

function fillCoastalGaps(grid, values, outline) {
  const filled = [...values];
  const estimated = new Set();
  const vertices = outlinePoints(outline);
  const touchesCity = (lat, lon) => {
    const [w, s] = [lon - grid.dlon / 2, lat - grid.dlat / 2];
    for (let i = 0; i <= 4; i++) {
      for (let j = 0; j <= 4; j++) {
        if (d3.geoContains(outline, [w + (j / 4) * grid.dlon, s + (i / 4) * grid.dlat])) return true;
      }
    }
    // Strips narrower than the sample spacing: an outline vertex inside the cell.
    return vertices.some(([x, y]) => x >= w && x <= w + grid.dlon && y >= s && y <= s + grid.dlat);
  };
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      const index = r * grid.cols + c;
      if (values[index] != null) continue;
      const neighbors = [];
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          const [rr, cc] = [r + dr, c + dc];
          if ((dr || dc) && rr >= 0 && rr < grid.rows && cc >= 0 && cc < grid.cols && values[rr * grid.cols + cc] != null) {
            neighbors.push(values[rr * grid.cols + cc]);
          }
        }
      }
      if (neighbors.length && touchesCity(grid.lat0 + r * grid.dlat, grid.lon0 + c * grid.dlon)) {
        filled[index] = d3.mean(neighbors);
        estimated.add(index);
      }
    }
  }
  return { values: filled, estimated };
}

// One GeoJSON square per cell with data; rings run clockwise, as d3 expects.
function cellFeatures(grid, values, estimated = new Set()) {
  const features = [];
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      const value = values[r * grid.cols + c];
      if (value == null) continue;
      const lat = grid.lat0 + r * grid.dlat;
      const lon = grid.lon0 + c * grid.dlon;
      const [w, e, s, n] = [lon - grid.dlon / 2, lon + grid.dlon / 2, lat - grid.dlat / 2, lat + grid.dlat / 2];
      features.push({
        type: "Feature",
        properties: { value, center: [lon, lat], estimated: estimated.has(r * grid.cols + c) },
        geometry: { type: "Polygon", coordinates: [[[w, s], [w, n], [e, n], [e, s], [w, s]]] },
      });
    }
  }
  return features;
}

// Cells clipped to the city limit, with the outline on top. Parts of the city
// with no data (water, coast) are hatched.
function CityCells({ grid, values, outline, unit }) {
  const containerRef = useRef(null);
  const width = useWidth(containerRef);
  const [hover, setHover] = useState(null);
  const clipId = useMemo(() => `city-clip-${Math.random().toString(36).slice(2)}`, []);

  const features = useMemo(() => {
    const gapFilled = fillCoastalGaps(grid, values, outline);
    return cellFeatures(grid, gapFilled.values, gapFilled.estimated);
  }, [grid, values, outline]);
  const { path, height } = useMemo(() => {
    if (!width) return { path: null, height: 0 };
    // Fit the outline into the width, capping the height for tall cities.
    const probe = d3.geoMercator().fitWidth(width, outline);
    const [[, y0], [, y1]] = d3.geoPath(probe).bounds(outline);
    const h = Math.min(MAX_HEIGHT, Math.ceil(y1 - y0));
    const projection = d3.geoMercator().fitExtent([[8, 8], [width - 8, h - 8]], outline);
    return { path: d3.geoPath(projection), height: h };
  }, [width, outline]);

  return (
    <div ref={containerRef} className="city-map" style={{ height: height || undefined }}>
      {path && (
        <svg width={width} height={height} role="img" aria-label="Map of the city's grid cells colored by temperature difference from the 1951–1980 normal">
          <defs>
            <clipPath id={clipId}>
              <path d={path(outline)} />
            </clipPath>
            <pattern id={`${clipId}-hatch`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="6" height="6" className="city-map-nodata" />
              <line x1="0" y1="0" x2="0" y2="6" className="city-map-hatch" />
            </pattern>
          </defs>
          {/* No-data background, visible wherever no cell covers the city */}
          <path d={path(outline)} fill={`url(#${clipId}-hatch)`} />
          <g clipPath={`url(#${clipId})`}>
            {features.map((f, i) => (
              <path
                key={i}
                d={path(f)}
                fill={anomalyColor(f.properties.value)}
                className="city-map-cell"
                onMouseMove={(e) => {
                  const box = containerRef.current.getBoundingClientRect();
                  setHover({ x: e.clientX - box.left, y: e.clientY - box.top, value: f.properties.value, index: i,
                             estimated: f.properties.estimated });
                }}
                onMouseLeave={() => setHover(null)}
              />
            ))}
            {hover && <path d={path(features[hover.index])} className="city-map-cell-hover" pointerEvents="none" />}
          </g>
          <path d={path(outline)} className="city-map-outline" />
        </svg>
      )}
      {hover && (
        <div className="us-map-tooltip" style={{ left: Math.min(hover.x + 14, width - 150), top: Math.max(hover.y - 46, 0) }}>
          <span className={hover.value >= 0 ? "legend-hot" : "legend-cool"}>{formatAnomaly(hover.value, unit)} vs. 1951–80</span>
          {hover.estimated && <span className="us-map-tooltip-note">Coastal edge: estimated from nearby land cells</span>}
        </div>
      )}
    </div>
  );
}

const shortDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const LAYER_TEXT = {
  today: {
    tab: "Today",
    source: (l) =>
      `Map: today's NWS forecast high${l.issued_at ? ` (issued ${new Date(l.issued_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })})` : ""} ` +
      "vs. the 1951–1980 normal (NOAA nClimGrid-Daily), in ~4 km cells; coastal edges estimated from nearby land cells. Gray-blue: no data.",
  },
  past_week: {
    tab: "Past week",
    source: (l) =>
      `Map: average daily high ${shortDate(l.start)}–${shortDate(l.end)} vs. the same days in 1951–1980 ` +
      "(NOAA nClimGrid-Daily, recent days preliminary" +
      (l.forecast_days ? `; the last ${l.forecast_days === 1 ? "day is that day's" : `${l.forecast_days} days are each day's`} NWS forecast until observations arrive` : "") +
      "), in ~4 km cells; coastal edges estimated from nearby land cells. Gray-blue: no data.",
  },
};

// The city's own headline number (°C anomaly), from the same data as the charts.
function headline(layerName, layer, weatherStrip, past100Days) {
  if (layerName === "today") {
    const featured = (weatherStrip ?? []).find((d) => d.is_featured && d.actual_anomaly != null);
    if (!featured) return null;
    const forecast = featured.source === "ndfd_forecast";
    return {
      valueC: featured.actual_anomaly,
      forecast,
      label: `${forecast ? "Today" : "Latest"}, ${shortDate(featured.date)} · ${forecast ? "forecast" : "observed"}`,
      period: "this date",
    };
  }
  if (layerName === "past_week" && layer) {
    // Same days as the map: nClimGrid, plus same-day forecasts for the days it hasn't reached yet.
    const days = (past100Days ?? []).filter(
      (d) => d.date >= layer.start && d.date <= layer.end && d.actual_anomaly != null,
    );
    if (!days.length) return null;
    const forecastDays = days.filter((d) => d.source === "ndfd_forecast").length;
    return {
      valueC: d3.mean(days, (d) => d.actual_anomaly),
      label: `Past week, ${shortDate(layer.start)}–${shortDate(layer.end)} · ${forecastDays ? "observed + forecast" : "observed"}`,
      period: "these days",
    };
  }
  return null;
}

// Shown when today and the past week point opposite ways (e.g. a cold front
// after a hot week), so a deep-blue today doesn't hide a hot week, or vice versa:
// both at least CONTRAST_MIN_F from normal, and at least CONTRAST_SWING_F apart.
const CONTRAST_MIN_F = 5;
const CONTRAST_SWING_F = 15;

function contrastNote(today, week, unit) {
  if (!today || !week) return null;
  const todayF = today.valueC * 1.8;
  const weekF = week.valueC * 1.8;
  if (Math.sign(todayF) === Math.sign(weekF) || Math.min(Math.abs(todayF), Math.abs(weekF)) < CONTRAST_MIN_F) return null;
  if (Math.abs(todayF - weekF) < CONTRAST_SWING_F) return null;
  const size = (valF) => formatAnomaly(Math.abs(valF), unit).replace("+", "");
  const way = (valF) => (valF > 0 ? "above" : "below");
  return (
    <>
      Today's high is {today.forecast ? "forecast " : ""}
      <strong className={todayF > 0 ? "legend-hot" : "legend-cool"}>{size(todayF)} {way(todayF)}</strong> normal, after a
      week that averaged <strong className={weekF > 0 ? "legend-hot" : "legend-cool"}>{size(weekF)} {way(weekF)}</strong>.
    </>
  );
}

export default function CityHero({ cityId, city, state, unit = "F", weatherStrip, past100Days }) {
  const [data, setData] = useState(null);
  const [layerName, setLayerName] = useState(null);
  const [status, setStatus] = useState(cityId ? "loading" : "none"); // loading | ready | none

  useEffect(() => {
    if (!cityId) {
      setStatus("none");
      return;
    }
    setStatus("loading");
    fetch(`${API_BASE}/city-map/${cityId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d || !d.outline) return setStatus("none");
        setData({ ...d, outline: mainParts(rewindForD3(d.outline)) });
        setLayerName(d.default_layer in d.layers ? d.default_layer : Object.keys(d.layers)[0]);
        setStatus("ready");
      })
      .catch(() => setStatus("none"));
  }, [cityId]);

  const layer = data?.layers?.[layerName];
  // Range over cells whose centers fall inside the city.
  const range = useMemo(() => {
    if (!layer || !data?.outline) return null;
    const inside = cellFeatures(data, layer.values)
      .filter((f) => d3.geoContains(data.outline, f.properties.center))
      .map((f) => f.properties.value);
    return inside.length ? [d3.min(inside), d3.max(inside)] : null;
  }, [data, layer]);
  const number = headline(layerName, layer, weatherStrip, past100Days);
  const contrast = contrastNote(
    headline("today", null, weatherStrip, past100Days),
    headline("past_week", data?.layers?.past_week, weatherStrip, past100Days),
    unit,
  );
  const title = (
    <>
      <p className="results-eyebrow">TEMPERATURE HISTORY</p>
      <h1 className="results-title">
        {city}, {state}
      </h1>
      <p className="results-subtitle">How much hotter has it gotten?</p>
    </>
  );

  // No map (station backend, or a city without one): the original centered header.
  if (status === "none") return <header className="results-header">{title}</header>;

  const tabs = data ? ["today", "past_week"].filter((name) => data.layers[name]) : [];
  const valueF = number ? number.valueC * 1.8 : null;

  return (
    <header className="city-hero">
      <div className="city-hero-text">
        {title}
        {number && (
          <div className="city-hero-stat">
            <span className="city-hero-label">{number.label}</span>
            <span className={`city-hero-number ${valueF >= 0 ? "legend-hot" : "legend-cool"}`}>
              {formatAnomaly(valueF, unit)}
            </span>
            <span className="city-hero-caption">
              {valueF >= 0 ? "warmer" : "cooler"} than the 1951–1980 normal for {number.period}
            </span>
            {contrast && <p className="city-hero-contrast">{contrast}</p>}
          </div>
        )}
        {tabs.length > 1 && (
          <div className="map-toggle city-hero-toggle" role="tablist" aria-label="Map period">
            {tabs.map((tab) => (
              <button
                key={tab}
                role="tab"
                aria-selected={tab === layerName}
                className={`map-toggle-option ${tab === layerName ? "map-toggle-option--active" : ""}`}
                onClick={() => setLayerName(tab)}
              >
                {LAYER_TEXT[tab].tab}
              </button>
            ))}
          </div>
        )}
        {range && (
          <p className="city-hero-range">
            Across the city:{" "}
            <strong className={range[0] >= 0 ? "legend-hot" : "legend-cool"}>{formatAnomaly(range[0], unit)}</strong>
            {range[0] !== range[1] && (
              <>
                {" "}to <strong className={range[1] >= 0 ? "legend-hot" : "legend-cool"}>{formatAnomaly(range[1], unit)}</strong>
              </>
            )}
          </p>
        )}
      </div>

      <div className="city-hero-map">
        {status === "loading" && <div className="city-hero-loading">Loading map…</div>}
        {status === "ready" && layer && (
          <>
            <CityCells grid={data} values={layer.values} outline={data.outline} unit={unit} />
            <MapLegend unit={unit} />
            <p className="city-hero-source">{LAYER_TEXT[layerName]?.source(layer)}</p>
          </>
        )}
      </div>
    </header>
  );
}
